// Runtime display state only. Never use these labels for URLs, roles, fields or API payloads.
export const ENTITIES = {
  reseller: { title: 'Reseller', singular: 'Reseller', plural: 'Resellers' },
  client: { title: 'Client', singular: 'Client', plural: 'Clients' },
  clientAdmin: { title: 'Client Admin', singular: 'Client Admin', plural: 'Client Admins' },
  clientUser: { title: 'Client User', singular: 'Client User', plural: 'Client Users' },
  case: { title: 'Case', singular: 'Case', plural: 'Cases' },
  businessSession: { title: 'Business Session', singular: 'Session', plural: 'Sessions' },
  deposition: { title: 'Deposition', singular: 'Deposition', plural: 'Depositions', group: 'sessionType' },
  witnessPrep: { title: 'Witness Prep', singular: 'Witness Prep', plural: 'Witness Preparations', group: 'sessionType' },
  trial: { title: 'Trial', singular: 'Trial', plural: 'Trials', group: 'sessionType' },
  mediation: { title: 'Mediation', singular: 'Mediation', plural: 'Mediations', group: 'sessionType' },
  arbitration: { title: 'Arbitration', singular: 'Arbitration', plural: 'Arbitrations', group: 'sessionType' },
};
let snapshot = { labels: {}, revision: 0, available: true };
const listeners = new Set();
export const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
export const getSnapshot = () => snapshot;
export const validName = (value, maxLength = 80) => typeof value === 'string' && value.trim().length > 0
  && Array.from(value).length <= maxLength && !/[<>]/u.test(value)
  && !Array.from(value).some(character => character.codePointAt(0) < 32 || character.codePointAt(0) === 127);
export function setTerminology(data) {
  // A GET started before a save must not roll back the newer successful revision.
  if (Number.isInteger(data?.revision) && data.revision < snapshot.revision) return;
  const labels = {};
  Object.keys(ENTITIES).forEach(key => {
    ['singular', 'plural'].forEach(form => {
      if (validName(data?.labels?.[key]?.[form], form === 'plural' ? 84 : 80)) {
        labels[key] = { ...labels[key], [form]: data.labels[key][form].trim() };
      }
    });
  });
  const next = { labels, revision: data?.revision || 0, available: data?.available !== false };
  if (JSON.stringify(next) === JSON.stringify(snapshot)) return;
  snapshot = next;
  listeners.forEach(listener => listener());
}
export const entityLabel = (key, form = 'singular', fallback = ENTITIES[key]?.[form] || key) =>
  snapshot.labels[key]?.[form] || fallback;

// Whole semantic phrases, longest first. Replacements are made once, never recursively.
// Generic account/participant "User" and authentication sessions are deliberately excluded.
const phrases = /\b(Client Admins|Client Admin|Client Users|Client User|Resellers|Reseller|Clients|Client|Cases|Case|Sessions|Session|Depositions|Deposition|Witness Preparations|Witness Preparation|Witness Preps|Witness Prep|WitnessPrep|Trials|Trial|Mediations|Mediation|Arbitrations|Arbitration)\b/gi;
const meanings = {
  depositions: ['deposition', 'plural'], deposition: ['deposition', 'singular'],
  'witness preparations': ['witnessPrep', 'plural'], 'witness preparation': ['witnessPrep', 'singular'],
  'witness preps': ['witnessPrep', 'plural'], 'witness prep': ['witnessPrep', 'singular'], witnessprep: ['witnessPrep', 'singular'],
  trials: ['trial', 'plural'], trial: ['trial', 'singular'],
  mediations: ['mediation', 'plural'], mediation: ['mediation', 'singular'],
  arbitrations: ['arbitration', 'plural'], arbitration: ['arbitration', 'singular'],
  'client admins': ['clientAdmin', 'plural'], 'client admin': ['clientAdmin', 'singular'],
  'client users': ['clientUser', 'plural'], 'client user': ['clientUser', 'singular'],
  resellers: ['reseller', 'plural'], reseller: ['reseller', 'singular'],
  clients: ['client', 'plural'], client: ['client', 'singular'],
  cases: ['case', 'plural'], case: ['case', 'singular'],
  sessions: ['businessSession', 'plural'], session: ['businessSession', 'singular'],
};
export function displayText(text) {
  if (typeof text !== 'string') return text;
  // This helper is called only at display sites, never on identifiers or record names.
  return text.replace(phrases, original => {
    const [key, form] = meanings[original.toLowerCase()];
    return snapshot.labels[key]?.[form] || original;
  });
}

// Adapt the existing heterogeneous config objects without overwriting their defaults.
// Nested proxies remain live even when dashboards retain module-level config shortcuts.
export function terminologyConfig(config, entityPaths = {}, prefix = '') {
  return new Proxy(config, {
    get(target, property) {
      const value = target[property];
      const path = prefix ? `${prefix}.${String(property)}` : String(property);
      if (value && typeof value === 'object') return terminologyConfig(value, entityPaths, path);
      if (typeof value !== 'string') return value;
      const descriptor = entityPaths[path];
      if (descriptor) {
        const [key, form, template] = descriptor;
        const custom = snapshot.labels[key]?.[form];
        return custom ? (template || '{name}').split('{name}').map(displayText).join(custom) : value;
      }
      // Only display properties; fields, sort keys, roles, routes and status values stay literal.
      if (/^(label|plural|pluralLabel|singular|pageHeading|breadcrumb|emptyMessage|managementLabel|fieldLabel|selectCasePrompt|backToCaseLabel|sessionOptionsLabel|depositionLabel|editDepositionLabel|addDepositionLabel)$/.test(String(property))) {
        return displayText(value);
      }
      return value;
    },
  });
}

// Dedicated client-user screens can name the role without changing generic account/participant users.
export function clientUserText(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/\b(Client Admins?|Client Users?|Resellers?|Clients?|Cases?|Sessions?|Users?|Depositions?|Witness Preparations?|Witness Preps?|WitnessPrep|Trials?|Mediations?|Arbitrations?)\b/gi, original => {
    const genericUser = /^users?$/i.test(original);
    const [key, form] = genericUser
      ? ['clientUser', original.toLowerCase() === 'users' ? 'plural' : 'singular']
      : meanings[original.toLowerCase()];
    return snapshot.labels[key]?.[form] || original;
  });
}

export function roleLabel(role, fallback = role) {
  const keys = { reseller: 'reseller', client_admin: 'clientAdmin', client_user: 'clientUser' };
  return keys[role] ? entityLabel(keys[role], 'singular', fallback) : displayText(fallback);
}

// CSV quoting alone does not prevent spreadsheet formulas in a configurable heading.
export const csvHeading = value => /^\s*[=+@-]/u.test(value) ? `'${value}` : value;

// English UI preview; the backend derives the saved plural using Laravel's inflector.
export function pluralName(name) {
  const value = name.trim();
  const irregular = { person: 'people', man: 'men', woman: 'women', child: 'children', mouse: 'mice', analysis: 'analyses', basis: 'bases', criterion: 'criteria' };
  return value.replace(/([\p{L}]+)$/u, word => {
    const lower = word.toLowerCase();
    let plural;
    if (['equipment', 'information', 'series', 'species', 'news', 'staff', 'fish', 'sheep'].includes(lower)) return word;
    if (irregular[lower]) plural = irregular[lower];
    else if (/[^aeiou]y$/i.test(word)) plural = word.slice(0, -1) + 'ies';
    else if (/(s|x|z|ch|sh)$/i.test(word)) plural = word + 'es';
    else plural = word + 's';
    if (word === word.toUpperCase()) return plural.toUpperCase();
    if (/^[A-Z]/.test(word) && irregular[lower]) return plural[0].toUpperCase() + plural.slice(1);
    return plural;
  });
}
